import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { Alert, Button, Card, PageHeader, Select, Spinner, errorMessage } from '../../ui/index.tsx';
import { useMoney } from '../products/data.ts';
import { buildLines, dayLabel, type ActivityItem, type ActivityLine } from './describe.ts';

const PAGE = 100;
const timeFormat = new Intl.DateTimeFormat('es-AR', { hour: '2-digit', minute: '2-digit' });

/** Historial de cambios del negocio (solo el dueño). Se consulta al servidor: requiere conexión. */
export function ActivityPage() {
  const { business, session } = useSession();
  const online = useOnline();
  const money = useMoney();
  const [filter, setFilter] = useState<'employees' | 'all' | string>('employees');
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const people = useLiveQuery(() => db.memberships.where('business_id').equals(business.id).toArray(), [business.id]);
  const devices = useLiveQuery(() => db.devices.where('business_id').equals(business.id).toArray(), [business.id]);

  const load = useCallback(
    async (before: ActivityItem | null) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc('activity_feed', {
          p_business_id: business.id,
          p_before_at: before?.at ?? null,
          p_before_key: before?.key ?? null,
          p_limit: PAGE,
          p_user_id: filter === 'employees' || filter === 'all' ? null : filter,
          p_only_employees: filter === 'employees',
        });
        if (rpcError) {
          console.warn('[actividad]', rpcError);
          // Los mensajes propios del servidor ya vienen en castellano; los técnicos no se muestran.
          throw new Error(
            rpcError.code === '42501' && /dueño/.test(rpcError.message)
              ? rpcError.message
              : 'No se pudo cargar la actividad. Probá de nuevo en unos minutos.',
          );
        }
        const page = (data ?? []) as ActivityItem[];
        setItems((prev) => (before ? [...prev, ...page] : page));
        setHasMore(page.length === PAGE);
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setLoading(false);
      }
    },
    [business.id, filter],
  );

  useEffect(() => {
    if (online && session.mode === 'online') void load(null);
  }, [load, online, session.mode]);

  const lines = useMemo(() => {
    const byUser = new Map((people ?? []).map((m) => [m.user_id, m.display_name]));
    const byMembership = new Map((people ?? []).map((m) => [m.id, m.display_name]));
    const byDevice = new Map((devices ?? []).map((d) => [d.id, d.name]));
    return buildLines(items, {
      userName: (id) => (id ? (byUser.get(id) ?? 'Usuario eliminado') : 'Sistema'),
      deviceName: (id) => (id ? (byDevice.get(id) ?? null) : null),
      membershipName: (id) => byMembership.get(id) ?? null,
      money,
    });
  }, [items, people, devices, money]);

  const days = useMemo(() => {
    const groups: Array<{ label: string; lines: ActivityLine[] }> = [];
    for (const line of lines) {
      const label = dayLabel(line.at);
      const last = groups[groups.length - 1];
      if (last?.label === label) last.lines.push(line);
      else groups.push({ label, lines: [line] });
    }
    return groups;
  }, [lines]);

  const canQuery = online && session.mode === 'online';

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <PageHeader title="Actividad" description="Quién cambió qué, y cuándo. Se ve la hora real del cambio, aunque se haya hecho sin internet." />

      <div className="flex flex-wrap items-center gap-2">
        <Select className="max-w-xs" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filtrar por persona">
          <option value="employees">Solo empleados</option>
          <option value="all">Todos</option>
          {(people ?? []).map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.display_name}
            </option>
          ))}
        </Select>
        {canQuery && (
          <Button variant="ghost" onClick={() => void load(null)} disabled={loading}>
            Actualizar
          </Button>
        )}
      </div>

      {!canQuery && (
        <Alert tone="warning">
          {online
            ? 'Entraste sin conexión: para ver la actividad, tocá "Cambiar de usuario" y volvé a entrar con internet.'
            : 'La actividad se consulta en el servidor: hace falta conexión a internet.'}
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}

      {canQuery && !loading && items.length === 0 && !error && (
        <Card className="text-center text-sm text-slate-500">
          {filter === 'employees' ? 'Los empleados todavía no hicieron cambios.' : 'Todavía no hay actividad.'}
        </Card>
      )}

      {days.map((day) => (
        <section key={day.label} className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-500 first-letter:uppercase">{day.label}</h2>
          <Card flush className="divide-y divide-slate-100">
            {day.lines.map((line) => (
              <ActivityRow key={line.key} line={line} />
            ))}
          </Card>
        </section>
      ))}

      {loading && (
        <div className="flex justify-center py-4 text-slate-500">
          <Spinner />
        </div>
      )}
      {hasMore && !loading && (
        <div className="text-center">
          <Button variant="secondary" onClick={() => void load(items[items.length - 1] ?? null)}>
            Ver más
          </Button>
        </div>
      )}
    </div>
  );
}

function ActivityRow({ line }: { line: ActivityLine }) {
  const meta = [timeFormat.format(line.at), line.device].filter(Boolean).join(' · ');
  const content = (
    <>
      <span className="font-medium">{line.who}</span> {line.text}
    </>
  );
  if (line.children.length === 0) {
    return (
      <div className="px-4 py-3 text-sm">
        <p>{content}</p>
        <p className="mt-0.5 text-xs text-slate-500">{meta}</p>
      </div>
    );
  }
  return (
    <details className="group px-4 py-3 text-sm">
      <summary className="cursor-pointer list-none">
        <p>
          {content} <span className="text-brand group-open:hidden">· ver detalle</span>
        </p>
        <p className="mt-0.5 text-xs text-slate-500">{meta}</p>
      </summary>
      <ul className="mt-2 space-y-1 border-l-2 border-slate-200 pl-3 text-slate-600">
        {line.children.map((c) => (
          <li key={c.key}>{c.text}</li>
        ))}
      </ul>
    </details>
  );
}
